import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@mlm/ui';
import {
  COACHING_LIBRARY_KIND_LABELS,
  COACHING_LIBRARY_KINDS,
  COACHING_LIBRARY_UPLOAD_KINDS,
  coachingLibraryCategories,
  type CoachingLibraryKind,
  type CoachingTier,
} from '@mlm/shared';
import { Audience } from '../components/tier-labels';
import { hasRole, useHasPerm } from '../lib/auth-store';
import {
  createLibraryLink,
  deleteLibraryItem,
  getTenantFeatures,
  listLibraryItems,
  patchLibraryItem,
  uploadLibraryItem,
  type LibraryItemRow,
} from '../lib/api';
import { AudienceSelect } from './coaching-tool-editors';

const MAX_UPLOAD_MB = 40;
const ACCEPT: Record<CoachingLibraryKind, string> = {
  video: '',
  audio: '.mp3,.m4a,audio/mpeg,audio/mp4',
  image: '.png,.jpg,.jpeg,image/png,image/jpeg',
  document: '.pdf,.docx,.xlsx,.pptx',
};
const hostOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};
const sizeLabel = (bytes: number | null) =>
  bytes ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : null;

/**
 * The content library (2026-10-13): videos, audio, images and documents in
 * one place, each with a description that is the ONLY thing Master Kala
 * reads about it. A video is link-only (Vimeo, YouTube or a direct file);
 * audio, images and documents may also be uploaded.
 */
export function LibraryTab() {
  const qc = useQueryClient();
  const canWrite = useHasPerm('coaching.content.write') || hasRole('super_admin');
  const items = useQuery({ queryKey: ['coaching-library'], queryFn: listLibraryItems });
  const features = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const categories = coachingLibraryCategories(features.data?.coaching);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['coaching-library'] });

  const [kind, setKind] = useState<CoachingLibraryKind>('video');
  const [mode, setMode] = useState<'link' | 'upload'>('link');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [premiumLevel, setPremiumLevel] = useState<CoachingTier>('free');
  const [editing, setEditing] = useState<LibraryItemRow | null>(null);
  const [removing, setRemoving] = useState<LibraryItemRow | null>(null);
  const [filter, setFilter] = useState<CoachingLibraryKind | 'all'>('all');

  const uploadable = COACHING_LIBRARY_UPLOAD_KINDS.includes(kind);
  const effectiveMode = uploadable ? mode : 'link';
  const reset = () => {
    setTitle('');
    setDescription('');
    setUrl('');
    setFile(null);
  };
  const add = useMutation({
    meta: { successMessage: 'Added to the library.' },
    mutationFn: async () => {
      const base = {
        kind,
        title: title.trim(),
        description: description.trim(),
        ...(category ? { category } : {}),
        premiumLevel,
      };
      if (effectiveMode === 'link') return createLibraryLink({ ...base, url: url.trim() });
      const form = new FormData();
      for (const [k, v] of Object.entries(base)) form.append(k, String(v));
      form.append('file', file!);
      return uploadLibraryItem(form);
    },
    onSuccess: () => {
      reset();
      invalidate();
    },
  });
  const save = useMutation({
    meta: { successMessage: 'Saved.' },
    mutationFn: () =>
      patchLibraryItem(editing!.id, {
        title: editing!.title.trim(),
        description: editing!.description.trim(),
        category: editing!.category || null,
        premiumLevel: editing!.premiumLevel,
      }),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });
  const toggle = useMutation({
    mutationFn: (row: LibraryItemRow) => patchLibraryItem(row.id, { active: !row.active }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    meta: { successMessage: 'Removed from the library.' },
    mutationFn: (id: string) => deleteLibraryItem(id),
    onSuccess: () => {
      setRemoving(null);
      invalidate();
    },
  });

  const tooBig = !!file && file.size > MAX_UPLOAD_MB * 1024 * 1024;
  const canAdd =
    title.trim().length >= 3 &&
    (effectiveMode === 'link' ? /^https:\/\//.test(url.trim()) : !!file && !tooBig) &&
    !add.isPending;
  const shown = (items.data ?? []).filter((i) => filter === 'all' || i.kind === filter);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Content library</CardTitle>
        <p className="text-sm text-muted-foreground">
          Everything the team collects for members, in one place. The description is what Master
          Kala reads when a member asks for something on a topic: write what the item is about and
          who it is for, in plain words. Members browse it under Align &rarr; Library and open items
          at the plan level you set. Categories are defined under Settings &rarr; Coaching.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {canWrite && (
          <div className="space-y-2 rounded-md border bg-muted/20 p-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Kind</label>
                <Select value={kind} onValueChange={(v) => setKind(v as CoachingLibraryKind)}>
                  <SelectTrigger className="h-8 w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COACHING_LIBRARY_KINDS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {COACHING_LIBRARY_KIND_LABELS[k]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {uploadable && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={effectiveMode === 'link' ? 'default' : 'outline'}
                    onClick={() => setMode('link')}
                  >
                    From a link
                  </Button>
                  <Button
                    size="sm"
                    variant={effectiveMode === 'upload' ? 'default' : 'outline'}
                    onClick={() => setMode('upload')}
                  >
                    Upload a file
                  </Button>
                </div>
              )}
            </div>
            {effectiveMode === 'link' ? (
              <div className="space-y-1">
                <Input
                  className="h-8"
                  placeholder={
                    kind === 'video' || kind === 'audio'
                      ? 'https://vimeo.com/123456789'
                      : 'https://example.com/guide.pdf'
                  }
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
                <p className="text-[11px] text-muted-foreground">
                  {kind === 'video' || kind === 'audio'
                    ? 'A Vimeo or YouTube link, or a direct media file on a host allowed under Settings → Courses. A video is always a link: keep films on Vimeo. The video’s own privacy settings must allow this site.'
                    : 'Any https address. Members open it in their browser.'}
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                <input
                  type="file"
                  accept={ACCEPT[kind]}
                  className="block text-sm"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                {tooBig && (
                  <p className="text-xs text-destructive">
                    Larger than {MAX_UPLOAD_MB} MB. Use a link instead.
                  </p>
                )}
              </div>
            )}
            <Input
              className="h-8"
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <Textarea
              rows={3}
              placeholder="What it is about and who it is for. Master Kala reads only this."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <div className="flex flex-wrap items-end gap-3">
              {categories.length > 0 && (
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Category</label>
                  <Select
                    value={category || '-'}
                    onValueChange={(v) => setCategory(v === '-' ? '' : v)}
                  >
                    <SelectTrigger className="h-8 w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="-">None</SelectItem>
                      {categories.map((c) => (
                        <SelectItem key={c.key} value={c.key}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <AudienceSelect value={premiumLevel} onChange={setPremiumLevel} />
              <Button size="sm" disabled={!canAdd} onClick={() => add.mutate()}>
                {add.isPending ? 'Adding…' : 'Add to the library'}
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          {COACHING_LIBRARY_KINDS.map((k) => (
            <Button
              key={k}
              size="sm"
              variant={filter === k ? 'default' : 'outline'}
              onClick={() => setFilter(k)}
            >
              {COACHING_LIBRARY_KIND_LABELS[k]}
            </Button>
          ))}
        </div>
        {items.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {items.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Nothing in the library yet. Members see an empty Library screen until you add something.
          </p>
        )}
        <div className="divide-y rounded-md border">
          {shown.map((row) =>
            editing?.id === row.id ? (
              <div key={row.id} className="space-y-2 p-3">
                <Input
                  className="h-8"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                />
                <Textarea
                  rows={3}
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                />
                <div className="flex flex-wrap items-end gap-3">
                  {categories.length > 0 && (
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Category</label>
                      <Select
                        value={editing.category || '-'}
                        onValueChange={(v) =>
                          setEditing({ ...editing, category: v === '-' ? null : v })
                        }
                      >
                        <SelectTrigger className="h-8 w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="-">None</SelectItem>
                          {categories.map((c) => (
                            <SelectItem key={c.key} value={c.key}>
                              {c.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <AudienceSelect
                    value={editing.premiumLevel}
                    onChange={(premiumLevel) => setEditing({ ...editing, premiumLevel })}
                  />
                  <Button
                    size="sm"
                    disabled={editing.title.trim().length < 3 || save.isPending}
                    onClick={() => save.mutate()}
                  >
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div
                key={row.id}
                className="flex flex-wrap items-start justify-between gap-3 p-3 text-sm"
              >
                <div className="min-w-0 space-y-1">
                  <p className={row.active ? '' : 'text-muted-foreground line-through'}>
                    {row.title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {COACHING_LIBRARY_KIND_LABELS[row.kind]}
                    {row.source === 'link' && row.url
                      ? ` · Link · ${hostOf(row.url)}`
                      : ' · Upload'}
                    {sizeLabel(row.sizeBytes) ? ` · ${sizeLabel(row.sizeBytes)}` : ''}
                    {row.category
                      ? ` · ${categories.find((c) => c.key === row.category)?.label ?? row.category}`
                      : ''}{' '}
                    · <Audience level={row.premiumLevel} />
                  </p>
                  {row.description && (
                    <p className="max-w-prose text-xs text-muted-foreground">{row.description}</p>
                  )}
                  {(row.previewUrl ?? row.url) && (
                    <a
                      href={row.previewUrl ?? row.url ?? '#'}
                      target="_blank"
                      rel="noopener"
                      className="text-xs underline underline-offset-2"
                    >
                      Open
                    </a>
                  )}
                </div>
                {canWrite && (
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing(row)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggle.mutate(row)}>
                      {row.active ? 'Hide' : 'Show'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRemoving(row)}>
                      Remove
                    </Button>
                  </div>
                )}
              </div>
            ),
          )}
        </div>
        <ConfirmDialog
          open={removing !== null}
          onOpenChange={(open) => !open && setRemoving(null)}
          title="Remove this item?"
          description={
            removing
              ? `“${removing.title}” leaves the library for members and Master Kala. An uploaded file is deleted after the grace period.`
              : ''
          }
          confirmLabel="Remove"
          loading={remove.isPending}
          onConfirm={async () => {
            if (removing) await remove.mutateAsync(removing.id);
          }}
        />
      </CardContent>
    </Card>
  );
}
